import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { 
  ArrowLeft, Package, Search, Plus, X, Check, 
  MapPin, Phone, MessageCircle, Tag, RefreshCw,
  AlertTriangle, Clock, Truck, DollarSign, Camera, Image, Sparkles, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// GenerIA Brand Logo
const GENERIA_LOGO = "https://customer-assets.emergentagent.com/job_inspeccion-pwa/artifacts/qhvfjidw_IMG_8814.jpeg";

// Mexican phone validation
// Valid Mexican area codes (most common)
const MEXICO_AREA_CODES = [
  // Major cities
  '55', '33', '81',  // CDMX, Guadalajara, Monterrey
  // Other states
  '222', '229', '228', '233', '235', '236', '237', '238', '241', '243', '244', '245', '246', '247', '248', '249',
  '271', '272', '273', '274', '275', '276', '278', '279', '281', '282', '283', '284', '285', '286', '287', '288',
  '294', '296', '297', '311', '312', '313', '314', '315', '316', '317', '319', '321', '322', '323', '324', '325',
  '326', '327', '328', '329', '331', '332', '333', '341', '342', '343', '344', '345', '346', '347', '348', '349',
  '351', '352', '353', '354', '355', '356', '357', '358', '359', '371', '372', '373', '374', '375', '376', '377',
  '378', '379', '381', '382', '383', '384', '385', '386', '387', '388', '389', '391', '392', '393', '394', '395',
  '411', '412', '413', '414', '415', '417', '418', '419', '421', '422', '423', '424', '425', '426', '427', '428',
  '429', '431', '432', '433', '434', '435', '436', '437', '438', '441', '442', '443', '444', '445', '447', '448',
  '449', '451', '452', '453', '454', '455', '456', '457', '458', '459', '461', '462', '463', '464', '465', '466',
  '467', '468', '469', '471', '472', '473', '474', '475', '476', '477', '478', '481', '482', '483', '484', '485',
  '486', '487', '488', '489', '492', '493', '494', '495', '496', '498', '499', '591', '592', '593', '594', '595',
  '596', '597', '598', '599', '612', '613', '614', '615', '616', '618', '621', '622', '623', '624', '625', '626',
  '627', '628', '629', '631', '632', '633', '634', '635', '636', '637', '638', '639', '641', '642', '643', '644',
  '645', '646', '647', '648', '649', '651', '652', '653', '656', '658', '659', '661', '662', '664', '665', '667',
  '668', '669', '671', '672', '673', '674', '675', '676', '677', '686', '687', '694', '695', '696', '697', '698',
  '711', '712', '713', '714', '715', '716', '717', '718', '719', '721', '722', '723', '724', '725', '726', '727',
  '728', '731', '732', '733', '734', '735', '736', '737', '738', '739', '741', '742', '743', '744', '745', '746',
  '747', '748', '749', '751', '753', '754', '755', '756', '757', '758', '759', '761', '762', '763', '764', '765',
  '766', '767', '768', '769', '771', '772', '773', '774', '775', '776', '777', '778', '779', '781', '782', '783',
  '784', '785', '786', '789', '791', '797', '811', '812', '818', '821', '823', '824', '825', '826', '827', '828',
  '829', '831', '832', '833', '834', '835', '836', '841', '842', '844', '845', '846', '861', '862', '864', '866',
  '867', '868', '869', '871', '872', '873', '877', '878', '891', '892', '894', '897', '899', '913', '914', '916',
  '917', '918', '919', '921', '922', '923', '924', '932', '933', '934', '936', '937', '938', '951', '953', '954',
  '958', '961', '962', '963', '964', '965', '966', '967', '968', '969', '971', '972', '981', '982', '983', '984',
  '985', '986', '987', '988', '991', '992', '993', '994', '995', '996', '997', '998', '999'
];

/**
 * Validate Mexican phone number
 * @param {string} phone - Phone number to validate
 * @returns {object} - { valid: boolean, message: string }
 */
const validateMexicanPhone = (phone) => {
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  
  // Check if it's 10 digits
  if (digits.length !== 10) {
    return { 
      valid: false, 
      message: 'El número debe tener 10 dígitos' 
    };
  }
  
  // Check for valid area code (2 or 3 digits)
  const twoDigitCode = digits.substring(0, 2);
  const threeDigitCode = digits.substring(0, 3);
  
  // Major cities use 2-digit codes, others use 3-digit
  const isValidAreaCode = MEXICO_AREA_CODES.includes(twoDigitCode) || 
                          MEXICO_AREA_CODES.includes(threeDigitCode);
  
  if (!isValidAreaCode) {
    return { 
      valid: false, 
      message: 'Código de área inválido' 
    };
  }
  
  return { valid: true, message: '' };
};

// ============= TENGO MODAL (with photo upload + AI) =============
const TengoModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1 = photo, 2 = confirm/edit
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [photos, setPhotos] = useState([]); // Array of base64 strings
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    whatsapp: '',
    material: '',
    cantidad: '',
    unidad: 'pza',
    condicion: 'nuevo',
    descripcion: '',
    precio: '',
    ubicacion: ''
  });

  const unidades = ['pza', 'm²', 'm³', 'ml', 'kg', 'ton', 'litro', 'rollo', 'bulto'];

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const maxPhotos = 3 - photos.length;
    const filesToProcess = files.slice(0, maxPhotos);
    
    for (const file of filesToProcess) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        setPhotos(prev => [...prev, base64]);
      };
      reader.readAsDataURL(file);
    }
  };

  // Analyze ALL photos together when user clicks Continue
  const analyzeAllPhotos = async () => {
    if (photos.length === 0) {
      setStep(2);
      return;
    }
    
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/oportunidades/analyze-photo`, {
        photos: photos  // Send all photos
      });
      
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          material: response.data.material || prev.material,
          cantidad: response.data.cantidad?.toString() || prev.cantidad,
          unidad: response.data.unidad || prev.unidad,
          condicion: response.data.condicion || prev.condicion,
          descripcion: response.data.descripcion || prev.descripcion
        }));
        toast.success(`GenerIA analizó ${photos.length} foto${photos.length > 1 ? 's' : ''} correctamente`);
      } else {
        toast.info('Completa los datos manualmente');
      }
      setStep(2);
    } catch (error) {
      console.error('Error analyzing photos:', error);
      toast.error('Error al analizar. Completa los datos manualmente.');
      setStep(2);
    } finally {
      setAnalyzing(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.nombre.trim() || !formData.whatsapp.trim()) {
      toast.error('Nombre y WhatsApp son requeridos');
      return;
    }
    
    // Validate Mexican phone number
    const phoneValidation = validateMexicanPhone(formData.whatsapp);
    if (!phoneValidation.valid) {
      toast.error(phoneValidation.message);
      return;
    }
    
    if (!formData.material.trim() || !formData.cantidad || !formData.ubicacion.trim()) {
      toast.error('Material, cantidad y zona son requeridos');
      return;
    }
    if (photos.length === 0) {
      toast.error('Agrega al menos una foto');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        user_id: `public-${Date.now()}`,
        user_name: formData.nombre.trim(),
        user_phone: formData.whatsapp.replace(/\D/g, ''),  // Store only digits
        material: formData.material.trim(),
        cantidad: parseFloat(formData.cantidad),
        unidad: formData.unidad,
        ubicacion: formData.ubicacion.trim(),
        descripcion: formData.descripcion?.trim() || null,
        condicion: formData.condicion,
        precio: formData.precio ? parseFloat(formData.precio) : null,
        fotos: photos
      };

      await axios.post(`${API}/oportunidades/tengo`, payload);
      toast.success('Oferta publicada. Pendiente de aprobación.');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      // Handle rate limit error
      if (error.response?.status === 429) {
        toast.error('Límite alcanzado, intenta más tarde');
      } else {
        toast.error('Error al publicar');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0D1520] rounded-2xl border border-[#1E3550] w-full max-w-lg max-h-[90vh] overflow-hidden" 
           data-testid="tengo-modal">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-[#1E3550] bg-gradient-to-r from-[#22C55E]/20 to-transparent">
          <div>
            <h2 className="text-xl font-bold text-[#22C55E]">Publicar Oferta</h2>
            <p className="text-[#5A7A9A] text-sm">
              {step === 1 ? 'Paso 1: Sube fotos del material' : 'Paso 2: Confirma los datos'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1E3550] rounded-lg text-[#E2EAF4]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-[#1E3550]">
          <div 
            className="h-full bg-[#22C55E] transition-all"
            style={{ width: step === 1 ? '50%' : '100%' }}
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          {step === 1 ? (
            /* STEP 1: Photo Upload */
            <>
              {/* Photo Preview Grid */}
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-[#1E3550]">
                      <img src={photo} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removePhoto(idx)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              {photos.length < 3 && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={analyzing}
                  className="w-full p-8 border-2 border-dashed border-[#22C55E]/50 rounded-xl hover:border-[#22C55E] transition-colors bg-[#22C55E]/5"
                >
                  {analyzing ? (
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-[#22C55E] animate-pulse" />
                      </div>
                      <p className="text-[#22C55E] font-semibold">GenerIA está analizando...</p>
                      <p className="text-[#5A7A9A] text-sm mt-1">Detectando material, cantidad y condición</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                        <Camera className="w-6 h-6 text-[#22C55E]" />
                      </div>
                      <p className="text-[#E2EAF4] font-semibold">
                        {photos.length === 0 ? 'Tomar o seleccionar fotos' : 'Agregar más fotos'}
                      </p>
                      <p className="text-[#5A7A9A] text-sm mt-1">
                        {photos.length === 0 
                          ? 'La IA detectará automáticamente el material' 
                          : `${3 - photos.length} fotos restantes`}
                      </p>
                    </div>
                  )}
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
              />

              {/* Info Box */}
              <div className="p-4 bg-[#0066FF]/10 border border-[#0066FF]/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-[#00CFFF] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-[#E2EAF4] font-medium">GenerIA Vision</p>
                    <p className="text-xs text-[#5A7A9A] mt-1">
                      Sube 1-3 fotos del material. Al continuar, GenerIA analizará todas las fotos 
                      para detectar tipo, cantidad, condición y descripción automáticamente.
                    </p>
                  </div>
                </div>
              </div>

              {/* Skip button if user wants to enter manually */}
              {photos.length === 0 && (
                <button
                  onClick={() => setStep(2)}
                  className="w-full text-center text-sm text-[#5A7A9A] hover:text-[#E2EAF4] py-2"
                >
                  Omitir fotos y completar manualmente
                </button>
              )}
            </>
          ) : (
            /* STEP 2: Confirm/Edit Data */
            <>
              {/* Photo thumbnails */}
              {photos.length > 0 && (
                <div className="flex gap-2 mb-4">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="w-16 h-16 rounded-lg overflow-hidden border border-[#1E3550]">
                      <img src={photo} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {photos.length < 3 && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-16 h-16 rounded-lg border border-dashed border-[#1E3550] flex items-center justify-center hover:border-[#22C55E]"
                    >
                      <Plus className="w-5 h-5 text-[#5A7A9A]" />
                    </button>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
              />

              {/* Contact Info Section */}
              <div className="p-4 bg-[#0066FF]/10 border border-[#0066FF]/30 rounded-lg space-y-3">
                <p className="text-sm font-medium text-[#00CFFF]">Tus datos de contacto</p>
                <div>
                  <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Tu nombre *</label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                    placeholder="Ej: Juan Pérez"
                    className="bg-[#060C18] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                    data-testid="input-nombre"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Tu WhatsApp * (10 dígitos)</label>
                  <Input
                    value={formData.whatsapp}
                    onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                    placeholder="Ej: 5512345678"
                    maxLength={15}
                    className="bg-[#060C18] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                    data-testid="input-whatsapp"
                  />
                  <p className="text-xs text-[#5A7A9A] mt-1">Número mexicano de 10 dígitos</p>
                </div>
              </div>

              {/* Material */}
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Tipo de Material *</label>
                <Input
                  value={formData.material}
                  onChange={(e) => setFormData(prev => ({ ...prev, material: e.target.value }))}
                  placeholder="Ej: Varilla corrugada 3/8"
                  className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                  data-testid="input-material"
                />
              </div>

              {/* Cantidad + Unidad */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Cantidad *</label>
                  <Input
                    type="number"
                    value={formData.cantidad}
                    onChange={(e) => setFormData(prev => ({ ...prev, cantidad: e.target.value }))}
                    placeholder="0"
                    className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                    data-testid="input-cantidad"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Unidad *</label>
                  <select
                    value={formData.unidad}
                    onChange={(e) => setFormData(prev => ({ ...prev, unidad: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4]"
                    data-testid="select-unidad"
                  >
                    {unidades.map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Condición */}
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-2">Condición *</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, condicion: 'nuevo' }))}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      formData.condicion === 'nuevo'
                        ? 'border-[#22C55E] bg-[#22C55E]/10 text-[#22C55E]'
                        : 'border-[#1E3550] text-[#5A7A9A] hover:border-[#22C55E]/50'
                    }`}
                  >
                    <div className="text-center">
                      <span className="font-semibold">Nuevo</span>
                      <p className="text-xs mt-1 opacity-70">Sin usar</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, condicion: 'sobrante' }))}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      formData.condicion === 'sobrante'
                        ? 'border-[#FF6B00] bg-[#FF6B00]/10 text-[#FF6B00]'
                        : 'border-[#1E3550] text-[#5A7A9A] hover:border-[#FF6B00]/50'
                    }`}
                  >
                    <div className="text-center">
                      <span className="font-semibold">Sobrante</span>
                      <p className="text-xs mt-1 opacity-70">De obra</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Descripción</label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Detalles adicionales del material..."
                  rows={2}
                  className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] placeholder:text-[#5A7A9A] resize-none"
                  data-testid="input-descripcion"
                />
              </div>

              <hr className="border-[#1E3550]" />

              {/* Precio */}
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Precio por unidad (MXN)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A7A9A]">$</span>
                  <Input
                    type="number"
                    value={formData.precio}
                    onChange={(e) => setFormData(prev => ({ ...prev, precio: e.target.value }))}
                    placeholder="0.00"
                    className="pl-8 bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
                    data-testid="input-precio"
                  />
                </div>
              </div>

              {/* Zona */}
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Zona General *</label>
                <Input
                  value={formData.ubicacion}
                  onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))}
                  placeholder="Ej: CDMX Norte, Monterrey, etc."
                  className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                  data-testid="input-ubicacion"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#1E3550] px-6 py-4 flex justify-between gap-3 bg-[#0A1422]">
          {step === 1 ? (
            <>
              <Button variant="ghost" onClick={onClose} className="text-[#E2EAF4] hover:bg-[#1E3550]">
                Cancelar
              </Button>
              <Button 
                onClick={analyzeAllPhotos}
                disabled={analyzing}
                className="bg-[#22C55E] hover:bg-[#16A34A]"
              >
                {analyzing ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                    Analizando...
                  </>
                ) : (
                  <>Continuar</>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep(1)} className="text-[#E2EAF4] hover:bg-[#1E3550]">
                Atrás
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#22C55E] hover:bg-[#16A34A]"
              >
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Publicar
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============= NECESITO MODAL (unchanged) =============
const NecesitoModal = ({ onClose, onSuccess, user }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: user?.name || '',
    whatsapp: user?.phone || '',
    material: '',
    cantidad: '',
    unidad: 'pza',
    presupuesto: '',
    ubicacion: '',
    descripcion: '',
    urgente: false
  });

  const unidades = ['pza', 'm²', 'm³', 'ml', 'kg', 'ton', 'litro', 'rollo', 'bulto'];

  const handleSubmit = async () => {
    // Validate name and phone for anonymous users
    if (!user) {
      if (!formData.nombre.trim() || !formData.whatsapp.trim()) {
        toast.error('Nombre y WhatsApp son requeridos');
        return;
      }
      
      // Validate Mexican phone number
      const phoneValidation = validateMexicanPhone(formData.whatsapp);
      if (!phoneValidation.valid) {
        toast.error(phoneValidation.message);
        return;
      }
    }
    
    if (!formData.material.trim() || !formData.cantidad || !formData.ubicacion.trim()) {
      toast.error('Material, cantidad y ubicación son requeridos');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        user_id: user?.id || `public-${Date.now()}`,
        user_name: user?.name || formData.nombre.trim(),
        user_phone: user?.phone || formData.whatsapp.replace(/\D/g, ''),
        material: formData.material.trim(),
        cantidad: parseFloat(formData.cantidad),
        unidad: formData.unidad,
        ubicacion: formData.ubicacion.trim(),
        descripcion: formData.descripcion?.trim() || null,
        presupuesto: formData.presupuesto ? parseFloat(formData.presupuesto) : null,
        urgente: formData.urgente
      };

      const response = await axios.post(`${API}/oportunidades/necesito`, payload);
      
      // Check if auto match was found
      if (response.data.auto_match) {
        const priceText = formData.presupuesto ? `$${parseFloat(formData.presupuesto).toLocaleString()}` : 'Sin presupuesto';
        const message = encodeURIComponent(
          `🤖 Match automático detectado:\nNECESITA: ${formData.material} · ${formData.ubicacion} · ${priceText}\nRevisa en Admin Dashboard`
        );
        window.open(`https://wa.me/${ADMIN_WHATSAPP.replace('+', '')}?text=${message}`, '_blank');
        toast.success('¡Match encontrado! Se notificó al admin.');
      } else {
        toast.success('Solicitud publicada exitosamente');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      // Handle rate limit error
      if (error.response?.status === 429) {
        toast.error('Límite alcanzado, intenta más tarde');
      } else {
        toast.error('Error al publicar');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0D1520] rounded-2xl border border-[#1E3550] w-full max-w-lg max-h-[90vh] overflow-hidden" 
           data-testid="necesito-modal">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-[#1E3550] bg-gradient-to-r from-[#0066FF]/20 to-transparent">
          <div>
            <h2 className="text-xl font-bold text-[#0066FF]">Publicar Solicitud</h2>
            <p className="text-[#5A7A9A] text-sm">Necesito material</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1E3550] rounded-lg text-[#E2EAF4]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          {/* Name and WhatsApp fields for anonymous users */}
          {!user && (
            <>
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Tu Nombre *</label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: Juan Pérez"
                  className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Tu WhatsApp * (10 dígitos)</label>
                <Input
                  value={formData.whatsapp}
                  onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                  placeholder="Ej: 5512345678"
                  maxLength={15}
                  className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                />
                <p className="text-xs text-[#5A7A9A] mt-1">Número mexicano de 10 dígitos</p>
              </div>
            </>
          )}
          
          <div>
            <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Material *</label>
            <Input
              value={formData.material}
              onChange={(e) => setFormData(prev => ({ ...prev, material: e.target.value }))}
              placeholder="Ej: Varilla corrugada 3/8"
              className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Cantidad *</label>
              <Input
                type="number"
                value={formData.cantidad}
                onChange={(e) => setFormData(prev => ({ ...prev, cantidad: e.target.value }))}
                placeholder="0"
                className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Unidad *</label>
              <select
                value={formData.unidad}
                onChange={(e) => setFormData(prev => ({ ...prev, unidad: e.target.value }))}
                className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4]"
              >
                {unidades.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Presupuesto (opcional)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A7A9A]">$</span>
              <Input
                type="number"
                value={formData.presupuesto}
                onChange={(e) => setFormData(prev => ({ ...prev, presupuesto: e.target.value }))}
                placeholder="0.00"
                className="pl-8 bg-[#0A1422] border-[#1E3550] text-[#E2EAF4]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Ubicación *</label>
            <Input
              value={formData.ubicacion}
              onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))}
              placeholder="Ej: CDMX, Monterrey, etc."
              className="bg-[#0A1422] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Descripción (opcional)</label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Detalles adicionales..."
              rows={3}
              className="w-full px-3 py-2 border border-[#1E3550] rounded-lg bg-[#0A1422] text-[#E2EAF4] placeholder:text-[#5A7A9A] resize-none"
            />
          </div>

          <label className="flex items-center gap-3 p-3 bg-[#FF6B00]/10 border border-[#FF6B00]/30 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={formData.urgente}
              onChange={(e) => setFormData(prev => ({ ...prev, urgente: e.target.checked }))}
              className="w-5 h-5 text-[#FF6B00] rounded"
            />
            <div>
              <span className="font-medium text-[#FF6B00]">Urgente</span>
              <p className="text-xs text-[#5A7A9A]">Marcar si necesitas el material lo antes posible</p>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="border-t border-[#1E3550] px-6 py-4 flex justify-end gap-3 bg-[#0A1422]">
          <Button variant="ghost" onClick={onClose} className="text-[#E2EAF4] hover:bg-[#1E3550]">
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="bg-[#0066FF] hover:bg-[#0052CC]"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Publicar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Admin WhatsApp contact
const ADMIN_WHATSAPP = "+525650192332";

// ============= LISTING DETAIL MODAL =============
const ListingDetailModal = ({ listing, type, onClose }) => {
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [interestData, setInterestData] = useState({
    nombre: '',
    whatsapp: ''
  });

  const handleSubmitInterest = async () => {
    if (!interestData.nombre.trim() || !interestData.whatsapp.trim()) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSubmitting(true);
    try {
      // Create match record in backend
      await axios.post(`${API}/oportunidades/interest`, {
        listing_id: listing.id,
        listing_type: type,
        material: listing.material,
        zona: listing.ubicacion,
        precio: listing.precio || listing.presupuesto || 0,
        seller_name: listing.user_name,
        buyer_name: interestData.nombre.trim(),
        buyer_whatsapp: interestData.whatsapp.trim()
      });

      // Send WhatsApp to Admin
      const priceText = listing.precio ? `$${listing.precio.toLocaleString()}` : 'Sin precio';
      const message = encodeURIComponent(
        `🔔 Match pendiente:\nTIENE: ${listing.material} · ${listing.ubicacion} · ${priceText}\nVENDEDOR: ${listing.user_name}\nINTERESADO: ${interestData.nombre}\nRevisa en Admin Dashboard`
      );
      window.open(`https://wa.me/${ADMIN_WHATSAPP.replace('+', '')}?text=${message}`, '_blank');

      setSubmitted(true);
      toast.success('¡Listo! Te contactaremos pronto.');
    } catch (error) {
      console.error('Error submitting interest:', error);
      toast.error('Error al enviar. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-[#0D1520] rounded-2xl border border-[#1E3550] w-full max-w-lg max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="listing-detail-modal"
      >
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between border-b border-[#1E3550] ${
          type === 'tengo' 
            ? 'bg-gradient-to-r from-[#22C55E]/20 to-transparent' 
            : 'bg-gradient-to-r from-[#0066FF]/20 to-transparent'
        }`}>
          <div className="flex items-center gap-2">
            <Package className={`w-6 h-6 ${type === 'tengo' ? 'text-[#22C55E]' : 'text-[#0066FF]'}`} />
            <span className={`font-bold text-lg ${type === 'tengo' ? 'text-[#22C55E]' : 'text-[#0066FF]'}`}>
              {type === 'tengo' ? 'TENGO' : 'NECESITO'}
            </span>
            {type === 'tengo' && listing.condicion && (
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                listing.condicion === 'nuevo' 
                  ? 'bg-[#22C55E]/20 text-[#22C55E]' 
                  : 'bg-[#FF6B00]/20 text-[#FF6B00]'
              }`}>
                {listing.condicion === 'nuevo' ? 'Nuevo' : 'Sobrante'}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1E3550] rounded-lg text-[#E2EAF4]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          {/* Photos */}
          {listing.fotos && listing.fotos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {listing.fotos.map((foto, idx) => (
                <div key={idx} className="aspect-square rounded-lg overflow-hidden border border-[#1E3550]">
                  <img src={foto} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* Material Name */}
          <h2 className="text-2xl font-bold text-[#E2EAF4]">{listing.material}</h2>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#0A1422] rounded-lg p-4 border border-[#1E3550]">
              <div className="flex items-center gap-2 text-[#5A7A9A] mb-1">
                <Tag className="w-4 h-4" />
                <span className="text-sm">Cantidad</span>
              </div>
              <p className="text-xl font-bold text-[#00CFFF]">{listing.cantidad} {listing.unidad}</p>
            </div>
            
            <div className="bg-[#0A1422] rounded-lg p-4 border border-[#1E3550]">
              <div className="flex items-center gap-2 text-[#5A7A9A] mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">Zona</span>
              </div>
              <p className="text-xl font-bold text-[#E2EAF4]">{listing.ubicacion}</p>
            </div>
          </div>

          {/* Price */}
          {(listing.precio || listing.presupuesto) && (
            <div className="bg-[#22C55E]/10 rounded-lg p-4 border border-[#22C55E]/30">
              <div className="flex items-center gap-2 text-[#22C55E] mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm">{type === 'tengo' ? 'Precio por unidad' : 'Presupuesto'}</span>
              </div>
              <p className="text-2xl font-bold text-[#22C55E]">
                ${(listing.precio || listing.presupuesto).toLocaleString()} MXN
              </p>
            </div>
          )}

          {/* Description */}
          {listing.descripcion && (
            <div>
              <p className="text-sm text-[#5A7A9A] mb-1">Descripción</p>
              <p className="text-[#E2EAF4]">{listing.descripcion}</p>
            </div>
          )}

          {/* Seller/Requester Info */}
          <div className="bg-[#0A1422] rounded-lg p-4 border border-[#1E3550]">
            <p className="text-sm text-[#5A7A9A] mb-1">Publicado por</p>
            <p className="text-lg font-semibold text-[#E2EAF4]">{listing.user_name}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#1E3550] px-6 py-4 bg-[#0A1422]">
          {submitted ? (
            /* Success State */
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-[#22C55E]" />
              </div>
              <p className="text-lg font-semibold text-[#E2EAF4]">¡Listo!</p>
              <p className="text-[#5A7A9A]">Te contactaremos pronto.</p>
            </div>
          ) : showInterestForm ? (
            /* Interest Form */
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Tu nombre *</label>
                <Input
                  value={interestData.nombre}
                  onChange={(e) => setInterestData(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: Juan Pérez"
                  className="bg-[#060C18] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                  data-testid="input-interest-nombre"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#5A7A9A] mb-1">Tu WhatsApp *</label>
                <Input
                  value={interestData.whatsapp}
                  onChange={(e) => setInterestData(prev => ({ ...prev, whatsapp: e.target.value }))}
                  placeholder="+52 55 1234 5678"
                  className="bg-[#060C18] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
                  data-testid="input-interest-whatsapp"
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="ghost"
                  onClick={() => setShowInterestForm(false)}
                  className="flex-1 text-[#E2EAF4] hover:bg-[#1E3550]"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSubmitInterest}
                  disabled={submitting}
                  className="flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white"
                  data-testid="btn-submit-interest"
                >
                  {submitting ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Enviar
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            /* Initial CTA Button */
            <Button 
              onClick={() => setShowInterestForm(true)}
              className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white py-6 text-lg"
              data-testid="btn-me-interesa"
            >
              <Check className="w-6 h-6 mr-2" />
              Me interesa
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============= LISTING CARD =============
const ListingCard = ({ listing, type, onClick }) => {
  const isUrgent = type === 'necesito' && listing.urgente;
  const isPending = listing.status === 'pending';

  return (
    <div 
      className={`bg-[#0D1520] rounded-xl border overflow-hidden cursor-pointer hover:border-[#00CFFF] transition-colors ${
        isUrgent ? 'border-[#FF6B00]' : 'border-[#1E3550]'
      }`} 
      onClick={onClick}
      data-testid={`listing-card-${listing.id}`}
    >
      {/* Header */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        type === 'tengo' ? 'bg-[#22C55E]/10' : 'bg-[#0066FF]/10'
      }`}>
        <div className="flex items-center gap-2">
          <Package className={`w-5 h-5 ${type === 'tengo' ? 'text-[#22C55E]' : 'text-[#0066FF]'}`} />
          <span className={`font-semibold ${type === 'tengo' ? 'text-[#22C55E]' : 'text-[#0066FF]'}`}>
            {type === 'tengo' ? 'TENGO' : 'NECESITO'}
          </span>
          {type === 'tengo' && listing.condicion && (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              listing.condicion === 'nuevo' 
                ? 'bg-[#22C55E]/20 text-[#22C55E]' 
                : 'bg-[#FF6B00]/20 text-[#FF6B00]'
            }`}>
              {listing.condicion === 'nuevo' ? 'Nuevo' : 'Sobrante'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isUrgent && (
            <span className="px-2 py-0.5 bg-[#FF6B00]/20 text-[#FF6B00] text-xs font-medium rounded-full flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              URGENTE
            </span>
          )}
          {isPending && (
            <span className="px-2 py-0.5 bg-[#FBBF24]/20 text-[#FBBF24] text-xs font-medium rounded-full flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Pendiente
            </span>
          )}
        </div>
      </div>

      {/* Photos (if TENGO has them) */}
      {type === 'tengo' && listing.fotos && listing.fotos.length > 0 && (
        <div className="flex gap-1 p-2 bg-[#0A1422]">
          {listing.fotos.slice(0, 3).map((foto, idx) => (
            <div key={idx} className="w-20 h-20 rounded overflow-hidden">
              <img src={foto} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-3">
        <h3 className="text-lg font-bold text-[#E2EAF4]">{listing.material}</h3>
        
        <div className="flex flex-wrap gap-3 text-sm">
          <div className="flex items-center gap-1 text-[#00CFFF]">
            <Tag className="w-4 h-4" />
            <span className="font-mono">{listing.cantidad} {listing.unidad}</span>
          </div>
          <div className="flex items-center gap-1 text-[#5A7A9A]">
            <MapPin className="w-4 h-4" />
            <span>{listing.ubicacion}</span>
          </div>
          {(listing.precio || listing.presupuesto) && (
            <div className="flex items-center gap-1 text-[#22C55E]">
              <DollarSign className="w-4 h-4" />
              <span>${(listing.precio || listing.presupuesto).toLocaleString()}</span>
            </div>
          )}
        </div>

        {listing.descripcion && (
          <p className="text-sm text-[#8BA3C7] line-clamp-2">{listing.descripcion}</p>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-[#1E3550]">
          <div className="text-sm text-[#5A7A9A]">
            <span className="text-[#E2EAF4]">{listing.user_name}</span>
          </div>
          <span className="text-xs text-[#00CFFF] flex items-center gap-1">
            Toca para ver más <ChevronRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </div>
  );
};

// ============= MAIN OPORTUNIDADES PAGE =============
export const OportunidadesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('tengo');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTengoModal, setShowTengoModal] = useState(false);
  const [showNecesitoModal, setShowNecesitoModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);

  const loadListings = async () => {
    setLoading(true);
    try {
      const endpoint = activeTab === 'tengo' ? '/oportunidades/tengo' : '/oportunidades/necesito';
      const response = await axios.get(`${API}${endpoint}`);
      setListings(response.data);
    } catch (error) {
      console.error('Error loading listings:', error);
      toast.error('Error al cargar listados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadListings();
  }, [activeTab]);

  const filteredListings = listings.filter(l =>
    l.material.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.ubicacion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePublish = (type) => {
    // No login required - anyone can publish
    if (type === 'tengo') {
      setShowTengoModal(true);
    } else {
      setShowNecesitoModal(true);
    }
  };

  const handleListingClick = (listing) => {
    setSelectedListing(listing);
  };

  return (
    <div className="min-h-screen bg-[#060C18]" data-testid="oportunidades-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#040810] to-[#060C18] border-b border-[#1E3550] text-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="text-[#E2EAF4] hover:bg-[#1E3550]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3 flex-1">
              <img src={GENERIA_LOGO} alt="GenerIA" className="w-10 h-10 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-[#E2EAF4]">GenerIA Oportunidades</h1>
                <p className="text-[#00CFFF] text-sm">Marketplace de Materiales</p>
              </div>
            </div>
            <div className="text-right">
              {isAuthenticated ? (
                <p className="font-semibold text-sm text-[#E2EAF4]">{user?.name}</p>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                  className="text-[#00CFFF] hover:bg-[#1E3550]"
                >
                  Iniciar Sesión
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-[#0D1520] border-b border-[#1E3550] sticky top-[72px] z-10">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex">
            <button
              onClick={() => setActiveTab('tengo')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors relative ${
                activeTab === 'tengo' ? 'text-[#22C55E]' : 'text-[#5A7A9A] hover:text-[#E2EAF4]'
              }`}
              data-testid="tab-tengo"
            >
              <div className="flex items-center justify-center gap-2">
                <Truck className="w-5 h-5" />
                TENGO
              </div>
              {activeTab === 'tengo' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#22C55E]" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('necesito')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors relative ${
                activeTab === 'necesito' ? 'text-[#0066FF]' : 'text-[#5A7A9A] hover:text-[#E2EAF4]'
              }`}
              data-testid="tab-necesito"
            >
              <div className="flex items-center justify-center gap-2">
                <Package className="w-5 h-5" />
                NECESITO
              </div>
              {activeTab === 'necesito' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0066FF]" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Search and Actions */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A7A9A]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por material o ubicación..."
              className="pl-10 bg-[#0D1520] border-[#1E3550] text-[#E2EAF4] placeholder:text-[#5A7A9A]"
              data-testid="search-input"
            />
          </div>
          <Button
            onClick={loadListings}
            variant="ghost"
            className="text-[#5A7A9A] hover:text-[#E2EAF4] hover:bg-[#1E3550]"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Publish Buttons */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Button
            onClick={() => handlePublish('tengo')}
            className="bg-[#22C55E] hover:bg-[#16A34A] py-6"
            data-testid="btn-new-tengo"
          >
            <Plus className="w-5 h-5 mr-2" />
            Publicar TENGO
          </Button>
          <Button
            onClick={() => handlePublish('necesito')}
            className="bg-[#0066FF] hover:bg-[#0052CC] py-6"
            data-testid="btn-new-necesito"
          >
            <Plus className="w-5 h-5 mr-2" />
            Publicar NECESITO
          </Button>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-[#5A7A9A] animate-spin" />
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="bg-[#0D1520] rounded-xl border border-[#1E3550] p-12 text-center">
            <Package className="w-12 h-12 text-[#5A7A9A] mx-auto mb-4 opacity-50" />
            <p className="text-[#5A7A9A]">
              {searchTerm 
                ? 'No se encontraron resultados' 
                : `No hay ofertas de ${activeTab === 'tengo' ? 'materiales disponibles' : 'materiales solicitados'}`
              }
            </p>
            <p className="text-sm text-[#5A7A9A] mt-2">
              ¡Sé el primero en publicar!
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredListings.map(listing => (
              <ListingCard 
                key={listing.id} 
                listing={listing} 
                type={activeTab}
                onClick={() => handleListingClick(listing)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Listing Detail Modal */}
      {selectedListing && (
        <ListingDetailModal
          listing={selectedListing}
          type={activeTab}
          onClose={() => setSelectedListing(null)}
        />
      )}

      {/* Tengo Modal */}
      {showTengoModal && (
        <TengoModal
          onClose={() => setShowTengoModal(false)}
          onSuccess={loadListings}
          user={user}
        />
      )}

      {/* Necesito Modal */}
      {showNecesitoModal && (
        <NecesitoModal
          onClose={() => setShowNecesitoModal(false)}
          onSuccess={loadListings}
          user={user}
        />
      )}
    </div>
  );
};

export default OportunidadesPage;
